import React from 'react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from 'recharts';

// ─────────────────────────────────────────────────────────────
// UI-ONLY BUILD — dummy data everywhere, no fetch/export wiring yet.
// Once the layout is signed off, each report's dummy arrays get
// swapped for real makeRequest2 calls, same pattern as the old
// FacilityUtilityReports.jsx (period -> params -> setX(response...)).
// ─────────────────────────────────────────────────────────────

export const formatCurrency = (value) =>
    `KSh ${Number(value ?? 0).toLocaleString()}`;

const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

// ── Launcher card — same card used on the Water reports landing page ──
export function ReportLauncherCard({ icon, title, description, onGenerate }) {
    return (
        <div className="col-md-4">
            <div className="card h-100">
                <div className="card-body d-flex flex-column">
                    <div className="mb-3">
                        <span className="avtar avtar-s bg-light-primary">
                            <i className={`ti ${icon} f-20`}></i>
                        </span>
                    </div>
                    <h5>{title}</h5>
                    <p className="text-muted flex-grow-1">{description}</p>
                    <button className="btn btn-primary w-100 mt-2" onClick={onGenerate}>
                        <i className="ti ti-report me-1"></i> View Report
                    </button>
                </div>
            </div>
        </div>
    );
}

// ── Modal shell ──
export function ReportModal({ show, onClose, title, subtitle, size = 'modal-xl', children }) {
    if (!show) return null;
    return (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
            <div className={`modal-dialog ${size} modal-dialog-scrollable`}>
                <div className="modal-content">
                    <div className="modal-header">
                        <div>
                            <h5 className="modal-title mb-0">{title}</h5>
                            {subtitle && <small className="text-muted">{subtitle}</small>}
                        </div>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>
                    <div className="modal-body">{children}</div>
                </div>
            </div>
        </div>
    );
}

export function SummaryCard({ bg, label, value }) {
    return (
        <div className="col-md-3">
            <div className={`card bg-light-${bg}`}>
                <div className="card-body">
                    <h6 className="mb-1">{label}</h6>
                    <h4 className="mb-0">{value}</h4>
                </div>
            </div>
        </div>
    );
}

export function ExportBar({ disabled, rows = [], columns = [], filename = 'report', title = 'Report' }) {
    const [exporting, setExporting] = React.useState(false);

    const handleExport = async (format) => {
        if (!rows.length) return;
        setExporting(true);
        try {
            if (format === 'csv') {
                const header = columns.map((c) => c.header).join(',');
                const body = rows.map((row) =>
                    columns.map((c) => {
                        const val = typeof c.accessor === 'function' ? c.accessor(row) : row[c.field];
                        const str = String(val ?? '').replace(/"/g, '""');
                        return `"${str}"`;
                    }).join(',')
                ).join('\n');
                const blob = new Blob([header + '\n' + body], { type: 'text/csv' });
                downloadBlob(blob, `${filename}.csv`);
            } else if (format === 'excel') {
                const XLSX = await import('xlsx');
                const data = rows.map((row) => {
                    const obj = {};
                    columns.forEach((c) => {
                        obj[c.header] = typeof c.accessor === 'function' ? c.accessor(row) : row[c.field];
                    });
                    return obj;
                });
                const ws = XLSX.utils.json_to_sheet(data);
                ws['!cols'] = columns.map(() => ({ wch: 20 }));
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, 'Report');
                XLSX.writeFile(wb, `${filename}.xlsx`);
            } else if (format === 'pdf') {
                const { default: jsPDF } = await import('jspdf');
                await import('jspdf-autotable');
                const doc = new jsPDF('p', 'mm', 'a4');
                doc.setFontSize(14);
                doc.text(title, 14, 15);
                doc.autoTable({
                    startY: 22,
                    head: [columns.map((c) => c.header)],
                    body: rows.map((row) =>
                        columns.map((c) => {
                            const val = typeof c.accessor === 'function' ? c.accessor(row) : row[c.field];
                            return String(val ?? '');
                        })
                    ),
                    styles: { fontSize: 8 },
                    headStyles: { fillColor: [30, 64, 175] },
                });
                doc.save(`${filename}.pdf`);
            }
        } catch (err) {
            console.error('Export error:', err);
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className="d-flex justify-content-end gap-2 mb-3">
            <button className="btn btn-outline-secondary btn-sm rounded-pill" disabled={disabled || exporting} onClick={() => handleExport('csv')}>
                {exporting ? '...' : 'CSV'}
            </button>
            <button className="btn btn-outline-success btn-sm rounded-pill" disabled={disabled || exporting} onClick={() => handleExport('excel')}>
                {exporting ? '...' : 'Excel'}
            </button>
            <button className="btn btn-outline-warning btn-sm rounded-pill" disabled={disabled || exporting} onClick={() => handleExport('pdf')}>
                {exporting ? '...' : 'PDF'}
            </button>
        </div>
    );
}

const QUARTERS = [
    { label: 'Q1 (Jan - Mar)', value: 'Q1' },
    { label: 'Q2 (Apr - Jun)', value: 'Q2' },
    { label: 'Q3 (Jul - Sep)', value: 'Q3' },
    { label: 'Q4 (Oct - Dec)', value: 'Q4' },
];

const currentYear = new Date().getFullYear();
const YEARS = [currentYear, currentYear - 1, currentYear - 2];

// Converts a period filter object into the query params the backend
// reports controllers expect (matches get_money_collected_report's
// resolvePeriodRange input: periodType, startDate/endDate, month,
// quarter/year).
export function periodToQueryParams(period) {
    const params = { periodType: period.periodType };
    if (period.periodType === 'dateRange') {
        if (period.startDate) params.startDate = period.startDate;
        if (period.endDate) params.endDate = period.endDate;
    } else if (period.periodType === 'month') {
        if (period.month) params.month = period.month;
    } else if (period.periodType === 'quarter') {
        params.quarter = period.quarter;
        params.year = period.year;
    }
    return params;
}

export function emptyPeriod() {
    return {
        periodType: 'month', // 'dateRange' | 'month' | 'quarter'
        startDate: '',
        endDate: '',
        month: '',
        quarter: 'Q1',
        year: String(currentYear),
    };
}

// ── Shared period filter — Date Range / Month / Quarter ──
export function PeriodFilter({ period, onChange }) {
    const update = (patch) => onChange({ ...period, ...patch });

    return (
        <div className="row g-2 align-items-end">
            <div className="col-md-3">
                <label className="form-label small text-muted mb-1">Period Type</label>
                <select className="form-control" value={period.periodType} onChange={(e) => update({ periodType: e.target.value })}>
                    <option value="dateRange">Date Range</option>
                    <option value="month">Month</option>
                    <option value="quarter">Quarter</option>
                </select>
            </div>

            {period.periodType === 'dateRange' && (
                <>
                    <div className="col-md-3">
                        <label className="form-label small text-muted mb-1">Start Date</label>
                        <input type="date" className="form-control" value={period.startDate} onChange={(e) => update({ startDate: e.target.value })} />
                    </div>
                    <div className="col-md-3">
                        <label className="form-label small text-muted mb-1">End Date</label>
                        <input type="date" className="form-control" value={period.endDate} onChange={(e) => update({ endDate: e.target.value })} />
                    </div>
                </>
            )}

            {period.periodType === 'month' && (
                <div className="col-md-3">
                    <label className="form-label small text-muted mb-1">Month</label>
                    <input type="month" className="form-control" value={period.month} onChange={(e) => update({ month: e.target.value })} />
                </div>
            )}

            {period.periodType === 'quarter' && (
                <>
                    <div className="col-md-3">
                        <label className="form-label small text-muted mb-1">Quarter</label>
                        <select className="form-control" value={period.quarter} onChange={(e) => update({ quarter: e.target.value })}>
                            {QUARTERS.map((q) => <option key={q.value} value={q.value}>{q.label}</option>)}
                        </select>
                    </div>
                    <div className="col-md-3">
                        <label className="form-label small text-muted mb-1">Year</label>
                        <select className="form-control" value={period.year} onChange={(e) => update({ year: e.target.value })}>
                            {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                        </select>
                    </div>
                </>
            )}
        </div>
    );
}

// ── 12-month consumption trend — bar chart ──
// data: [{ month: 'Aug 2025', consumption: 1234 }, ...]
export function ConsumptionTrendChart({ data, unitLabel = 'm³', barColor = '#1976D2' }) {
    return (
        <ResponsiveContainer width="100%" height={320}>
            <BarChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} label={{ value: unitLabel, angle: -90, position: 'insideLeft', fontSize: 11 }} />
                <Tooltip formatter={(value) => [`${value.toLocaleString()} ${unitLabel}`, 'Consumption']} />
                <Bar dataKey="consumption" fill={barColor} radius={[4, 4, 0, 0]} />
            </BarChart>
        </ResponsiveContainer>
    );
}

// ── Dummy data generators ──────────────────────────────────────

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Last 12 months ending on the current month, with a bit of variance.
export function generateTrendData(base = 1000, variance = 400) {
    const now = new Date();
    const result = [];
    for (let i = 11; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        result.push({
            month: `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`,
            consumption: Math.round(base + Math.random() * variance),
        });
    }
    return result;
}

export function generateMeterConsumptionRows(prefix, count, unitPrefix = 'Unit') {
    return Array.from({ length: count }, (_, i) => {
        const prev = Math.round(200 + Math.random() * 300);
        const usage = Math.round(20 + Math.random() * 80);
        return {
            meterNumber: `${prefix}-${1000 + i}`,
            unitName: `${unitPrefix} ${String.fromCharCode(65 + (i % 5))}${Math.floor(i / 5) + 1}`,
            customerName: `Customer ${i + 1}`,
            previousReading: prev,
            currentReading: prev + usage,
            consumption: usage,
            period: 'Aug 2026',
        };
    });
}

export function generateMoneyCollectedRows(prefix, count) {
    return Array.from({ length: count }, (_, i) => {
        const topUps = 1 + Math.floor(Math.random() * 6);
        const total = topUps * (500 + Math.round(Math.random() * 1500));
        return {
            accountNumber: `ACC-${prefix}-${2000 + i}`,
            customerName: `Customer ${i + 1}`,
            meterNumber: `${prefix}-${1000 + i}`,
            transactionCount: topUps,
            totalCollected: total,
            lastTopUpDate: new Date(2026, 7, (i % 28) + 1).toLocaleDateString(),
        };
    });
}

export function generateInvoiceRows(prefix, count) {
    const statuses = ['Paid', 'Unpaid', 'Partially Paid', 'Overdue'];
    return Array.from({ length: count }, (_, i) => {
        const billed = 800 + Math.round(Math.random() * 2500);
        const paid = Math.random() > 0.3 ? billed : Math.round(billed * Math.random());
        return {
            invoiceNumber: `INV-${prefix}-${5000 + i}`,
            meterNumber: `${prefix}-${1000 + i}`,
            customerName: `Customer ${i + 1}`,
            unitName: `Unit ${String.fromCharCode(65 + (i % 5))}${Math.floor(i / 5) + 1}`,
            totalAmount: billed,
            amountPaid: paid,
            balance: billed - paid,
            status: statuses[i % statuses.length],
            dateIssued: new Date(2026, 7, (i % 28) + 1).toLocaleDateString(),
            dueDate: new Date(2026, 7, ((i % 28) + 1) + 14).toLocaleDateString(),
        };
    });
}

export function generateFacilityPeriodRows(count = 6) {
    const now = new Date();
    return Array.from({ length: count }, (_, i) => {
        const d = new Date(now.getFullYear(), now.getMonth() - (count - 1 - i), 1);
        return {
            periodLabel: `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`,
            metersCount: 40 + Math.round(Math.random() * 10),
            totalConsumption: Math.round(1000 + Math.random() * 500),
            totalBilled: Math.round(60000 + Math.random() * 30000),
        };
    });
}